import { z } from 'zod';
import { DamageDieSchema } from './classDefinition';
import { ItemSchema } from './equipment';
import { StatIdSchema, StatModifierSchema } from './stats';

/**
 * Sistema Seguaci (Gregari / Evocazioni / Compagno Animale) — FI-8a pilota.
 *
 * Il manuale V1 (rr. 1267-1426) unifica sotto un'unica meccanica tre categorie
 * di "seguaci del PG": gregari ingaggiati, evocazioni magiche e compagni
 * animali. Distinguono solo due assi: presenza/assenza di **Costo** (motivo
 * narrativo da soddisfare) e di **Lealtà** (modificatore della mossa *Dare
 * Ordini a un Gregario*).
 *
 * Questa è la PR pilota: vengono introdotti tutti gli schemi del sistema, ma
 * solo **un template** è popolato nei dati — *Compagno Animale* del Ranger.
 * Gli altri 5 template (Famiglio Mago, Supporto Elementale Druido, Animare
 * Zombi Sciamano, Braccio Destro Warlock, Banda di Guerra Barbaro, Cavalcatura
 * Paladino) verranno in PR successive, una per classe.
 *
 * SSOT di design: `dev-workspace/docs/FI-8-Followers-SSOT.md`.
 */

// ============================================================================
// 1. Tipi di seguace
// ============================================================================

/**
 * Tassonomia dei seguaci. Il `type` è il discriminante principale: determina
 * la presenza/assenza dei campi `loyalty` e `cost`, nonché le regole di
 * gioco applicate (un gregario ha bisogno di *dare ordini*, un'evocazione no).
 *
 * - **hireling**: gregario classico ingaggiato (Lealtà + Costo). Es. Braccio
 *   Destro del Warlock.
 * - **summon**: evocazione magica. Tipicamente niente Lealtà; il Costo è
 *   variabile (presente per Supporto Elementale del Druido; assente per
 *   Animare Zombi dello Sciamano).
 * - **animal-companion**: animale/costrutto fedele a vita (no Costo, no
 *   Lealtà). Es. Compagno Animale del Ranger, Famiglio del Mago.
 * - **undead**: non morto evocato. Es. Animare Zombi.
 * - **collective**: gregario "collettivo" rappresentato da più membri (i PF
 *   indicano i membri attivi). Es. Banda di Guerra del Barbaro.
 * - **mount**: cavalcatura fedele. Es. Richiama Cavalcatura del Paladino.
 */
export const FollowerTypeSchema = z.enum([
  'hireling',
  'summon',
  'animal-companion',
  'undead',
  'collective',
  'mount',
]);
export type FollowerType = z.infer<typeof FollowerTypeSchema>;

// ============================================================================
// 2. Costo, Lealtà
// ============================================================================

/**
 * Motivazioni canoniche del Costo (manuale V1 r. 1313-1315). La lista è
 * **aperta**: il GM può inventare Costi specifici (es. «portare avanti i
 * piani del patrono» per il Braccio Destro del Warlock). Il campo
 * {@link FollowerCostSchema}.motivation è una stringa libera; questi sono
 * solo i valori suggeriti dalla UI.
 */
export const KNOWN_COST_MOTIVATIONS = [
  'Il Brivido della Vittoria',
  'Soldi',
  'Conoscenza e Scoperte',
  'Fama e Gloria',
  'Dissolutezza',
] as const;

/**
 * Costo che il PG deve soddisfare per mantenere il gregario soddisfatto.
 * Assente per evocazioni "ciecamente fedeli" e per i compagni animali.
 *
 * Stato runtime:
 *  - `paidThisSession`: tracker del pagamento (resettato ad ogni sessione).
 *  - `suspended`: per casi come il Druido «ben disposto» del Supporto
 *    Elementale, dove l'evocazione non richiede Costo per via di una scelta
 *    fatta al reclutamento.
 */
export const FollowerCostSchema = z.object({
  /** Motivazione del Costo (stringa libera). Esempi in {@link KNOWN_COST_MOTIVATIONS}. */
  motivation: z.string().min(1).max(200),
  /** Se `true`, il Costo è sospeso per regola della mossa-source (es. Druido «ben disposto»). */
  suspended: z.boolean().default(false),
  /** Runtime: il PG ha pagato il Costo nella sessione corrente? */
  paidThisSession: z.boolean().default(false),
});
export type FollowerCost = z.infer<typeof FollowerCostSchema>;

// ============================================================================
// 3. Abilità + mosse-gregario
// ============================================================================

/**
 * Le 9 Abilità codificate del manuale (rr. 1346-1426). Ognuna è un "mestiere"
 * con 3 mosse a Rango crescente (R1, R2, R3).
 */
export const FollowerAbilityIdSchema = z.enum([
  'adepto',
  'animista',
  'battitore',
  'combattente',
  'guardia-del-corpo',
  'menestrello',
  'sacerdote',
  'scassinatore',
  'scout',
]);
export type FollowerAbilityId = z.infer<typeof FollowerAbilityIdSchema>;

/** Rango di un'Abilità (1-3). Vincolo: rank ≤ livello del gregario. */
export const FollowerSkillRankSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export type FollowerSkillRank = z.infer<typeof FollowerSkillRankSchema>;

/**
 * Una mossa-gregario all'interno di un'Abilità. Le mosse vivono nel
 * catalogo `src/data/followers/skills/`; qui modelliamo solo la struttura.
 *
 * **Pattern «sostituisce»** (manuale rr. 1371, 1377): alcune mosse di Rango
 * superiore sostituiscono completamente l'effetto di una di Rango inferiore.
 * Esempi: Battitore R3 sostituisce R1; Combattente R2 sostituisce R1. Il
 * campo {@link FollowerAbilityMoveSchema}.replacesRank esprime il vincolo.
 */
export const FollowerAbilityMoveSchema = z.object({
  /** Rango della mossa entro la sua Abilità (1, 2 o 3). */
  rank: FollowerSkillRankSchema,
  /** Trigger della mossa, formula "Quando…". */
  trigger: z.string().min(1),
  /** Effetto completo (markdown). */
  description: z.string().min(1),
  /**
   * Se definito, questa mossa sostituisce quella del Rango indicato dentro
   * la stessa Abilità (manuale rr. 1371, 1377). Quando il gregario raggiunge
   * il Rango di questa mossa, perde l'effetto del Rango sostituito.
   */
  replacesRank: z.union([z.literal(1), z.literal(2)]).optional(),
});
export type FollowerAbilityMove = z.infer<typeof FollowerAbilityMoveSchema>;

/**
 * Definizione completa di un'Abilità (3 mosse, una per Rango).
 *
 * Le `ranks` sono indicizzate per Rango (sempre tre entry, una con
 * `rank: 1`, una con `rank: 2`, una con `rank: 3`).
 */
export const FollowerAbilitySchema = z
  .object({
    id: FollowerAbilityIdSchema,
    /** Etichetta visibile (es. "Guardia del Corpo"). */
    label: z.string().min(1).max(50),
    /** Breve descrizione del "mestiere" (manuale, riga di apertura). */
    description: z.string().min(1),
    /** Esattamente 3 mosse, una per Rango. */
    ranks: z.array(FollowerAbilityMoveSchema).length(3),
  })
  .refine(
    (a) => {
      const ranks = a.ranks.map((m) => m.rank).sort((x, y) => x - y);
      return ranks[0] === 1 && ranks[1] === 2 && ranks[2] === 3;
    },
    {
      message: "un'Abilità deve avere esattamente una mossa per ogni Rango (1, 2, 3)",
      path: ['ranks'],
    },
  );
export type FollowerAbility = z.infer<typeof FollowerAbilitySchema>;

/**
 * Riferimento a un'Abilità nello stato runtime del gregario: quale Abilità,
 * a quale Rango, se è quella primaria, e tracker di mosse "consumate" per
 * accampamento/sessione.
 *
 * Vincolo cross-field validato runtime (non in schema): `rank ≤
 * follower.stats.level` (manuale r. 1344, «*nessun Rango può essere
 * superiore al suo livello da gregario. Mai.*»). Inoltre l'Abilità
 * `isPrimary: true` può essere al massimo una.
 */
export const FollowerSkillSchema = z.object({
  abilityId: FollowerAbilityIdSchema,
  rank: FollowerSkillRankSchema,
  /** L'Abilità "principale" del gregario (manuale rr. 1290-1296). */
  isPrimary: z.boolean().default(false),
  /** Runtime: mosse usate in questa Abilità nella sessione di accampamento corrente. */
  movesUsedThisCamp: z.array(z.string()).default([]),
  /** Runtime: mosse usate in questa Abilità nella sessione di gioco corrente. */
  movesUsedThisSession: z.array(z.string()).default([]),
});
export type FollowerSkill = z.infer<typeof FollowerSkillSchema>;

// ============================================================================
// 4. Capacità utili (Traits)
// ============================================================================

/**
 * Effetti meccanici opzionali di una capacità utile. Il manuale (r. 1322-1338)
 * descrive 11 esempi di capacità, alcuni con effetto numerico (combattente
 * esperto → d8, robusto → +3 PF), alcuni puramente narrativi (vede al buio,
 * ha le ali), uno che concede una mossa di classe (devoto a una divinità →
 * *Scacciare Non Morti*).
 *
 * Tutti i campi sono opzionali; una capacità "narrativa" pura ha `effects:
 * undefined` o `effects: {}`.
 */
export const FollowerTraitEffectsSchema = z
  .object({
    /** Sovrascrive il `damageDie` del gregario (es. "combattente esperto" → d8). */
    damageDieOverride: DamageDieSchema.optional(),
    /** Modifica al Carico base (es. "instancabile" → +3, da 6 a 9). */
    loadDelta: z.number().int().optional(),
    /** Modifica all'Armatura del gregario (es. "pelle squamosa" → +1). */
    armorDelta: z.number().int().optional(),
    /** Modifica diretta ai PF max (es. "robusto" → +3). */
    hpDelta: z.number().int().optional(),
    /**
     * **Decisione D6 (SSOT)**: una capacità utile può concedere una mossa
     * di classe al gregario (manuale r. 1338, «devoto a una divinità: può
     * usare la mossa *Scacciare Non Morti* come un Chierico»).
     * Il valore è l'ID della mossa nel catalogo principale.
     */
    grantsMoveId: z.string().min(1).optional(),
  })
  .partial();
export type FollowerTraitEffects = z.infer<typeof FollowerTraitEffectsSchema>;

/**
 * Una capacità utile applicata al gregario. La lista è aperta: oltre agli
 * 11 esempi del manuale (vedi `KNOWN_FOLLOWER_TRAITS` in
 * `src/data/followers/traits.ts`), il GM può inventarne altre.
 */
export const FollowerTraitSchema = z.object({
  /** ID semantico stabile (es. 'combattente-esperto', 'robusto', 'devoto-divinita'). */
  id: z.string().min(1).max(50),
  /** Etichetta visibile (es. "Combattente esperto"). */
  label: z.string().min(1).max(100),
  description: z.string().optional(),
  /** Effetti meccanici opzionali. Una capacità puramente narrativa non ne ha. */
  effects: FollowerTraitEffectsSchema.optional(),
});
export type FollowerTrait = z.infer<typeof FollowerTraitSchema>;

// ============================================================================
// 5. Stats del gregario
// ============================================================================

/**
 * Modificatori di Caratteristica del gregario (range -3..+3 come per il PG).
 * Differente da {@link StatsSchema} che memorizza i **punteggi** (3-18):
 * qui sono i modificatori, perché il manuale per i gregari fornisce
 * direttamente i mod ("+1 a tutti", "+2 a uno scelto").
 */
export const FollowerModifiersSchema = z.object({
  forza: StatModifierSchema,
  destrezza: StatModifierSchema,
  costituzione: StatModifierSchema,
  intelligenza: StatModifierSchema,
  saggezza: StatModifierSchema,
  carisma: StatModifierSchema,
});
export type FollowerModifiers = z.infer<typeof FollowerModifiersSchema>;

/**
 * Regola di calcolo dei PF max del gregario. Tre varianti dal manuale:
 *
 * - **fixed**: PF max specifici dalla mossa-source, NON scalano con il
 *   livello del PG (es. Famiglio del Mago: 5 PF a livello 3). `value` è
 *   il numero esatto.
 * - **byLevel**: PF derivati dal livello del gregario seguendo la tabella
 *   del manuale (L1→5, L2→10, L3→15).
 * - **scaling**: formula `base + perCharacterLevel × livello_PG`, opzionalmente
 *   limitata da `max`. Es. Compagno Animale del Ranger: `1 + 2 × lvl`, max 21.
 *   Es. Braccio Destro del Warlock: `1 + 2 × lvl`, max 21. Es. Supporto
 *   Elementale del Druido «resistente»: `0 + 2 × lvl`, max +20 sopra base.
 */
export const FollowerHpRuleSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('fixed'), value: z.number().int().positive() }),
  z.object({ type: z.literal('byLevel') }),
  z.object({
    type: z.literal('scaling'),
    base: z.number().int().nonnegative(),
    perCharacterLevel: z.number().int().positive(),
    max: z.number().int().positive().optional(),
  }),
]);
export type FollowerHpRule = z.infer<typeof FollowerHpRuleSchema>;

/** Livello del gregario (1-3). */
export const FollowerLevelSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export type FollowerLevel = z.infer<typeof FollowerLevelSchema>;

/**
 * Statistiche di base del gregario. I valori derivati (PF max, Carico
 * effettivo, dado di Danno effettivo dopo trait, Armatura) sono calcolati
 * dal service `computeFollowerStats` applicando le `traits.effects` su
 * questi campi base.
 */
export const FollowerStatsSchema = z.object({
  level: FollowerLevelSchema,
  modifiers: FollowerModifiersSchema,
  hpRule: FollowerHpRuleSchema,
  damageDie: DamageDieSchema,
  /**
   * Capacità di Carico del gregario. Numero per la maggior parte dei casi
   * (manuale base: 6); `'narrative'` per casi come la Banda di Guerra del
   * Barbaro, dove il Carico è "fiction-based" (GM definisce volta per volta).
   */
  load: z.union([z.number().int().nonnegative(), z.literal('narrative')]),
  /**
   * Etichette del gregario stesso (non dell'equipaggiamento). Esempi:
   * *fatto di fuoco*, *immune a fuoco*, *vulnerabile a acqua* (Supporto
   * Elementale Druido); *ha le ali*, *vede al buio* (capacità utili → tag
   * narrativi).
   */
  tags: z.array(z.string()).default([]),
});
export type FollowerStats = z.infer<typeof FollowerStatsSchema>;

/**
 * Limiti massimi applicabili alle capacità utili scalanti. Manuale (r. 5254):
 * il Compagno Animale può avere al massimo d10 come dado di Danno, +2
 * Armatura e +6 Carico anche prendendo più volte le capacità *feroce*,
 * *tenace*, *resistente*. Si applica al calcolo runtime in
 * `computeFollowerStats`.
 */
export const FollowerCapabilityCapsSchema = z
  .object({
    damageDieMax: DamageDieSchema.optional(),
    armorMax: z.number().int().nonnegative().optional(),
    loadMax: z.number().int().nonnegative().optional(),
  })
  .partial();
export type FollowerCapabilityCaps = z.infer<typeof FollowerCapabilityCapsSchema>;

// ============================================================================
// 6. Follower completo
// ============================================================================

/**
 * Un seguace concreto presente sulla scheda di un PG. È un mini-personaggio:
 * identità + stats + Abilità + capacità + inventario + stato runtime.
 *
 * **Vincoli applicativi** (non enforced dallo schema, validati a livello
 * service in `assertFollowerInvariants`):
 *  - per ogni `skill`, `skill.rank ≤ stats.level`
 *  - al massimo un `skill.isPrimary: true`
 *  - se `type ∈ {summon, animal-companion, mount, undead}`, `loyalty` e `cost`
 *    sono tipicamente `undefined` (eccezione: Supporto Elementale Druido =
 *    `summon` con `cost` definito)
 *  - `currentHp ≤ maxHp` (maxHp è derivato, non memorizzato; per ora invariante
 *    runtime)
 */
export const FollowerSchema = z.object({
  /** ID univoco di questa istanza (UUID o equivalente). */
  id: z.string().min(1),
  /** ID della mossa di classe che lo ha creato (es. 'ranger.compagno-animale'). */
  sourceMoveId: z.string().min(1),
  /** ID del template che ne ha definito i parametri (vedi `src/data/followers/`). */
  templateId: z.string().min(1),
  type: FollowerTypeSchema,
  /** Identità: nome (dato dal PG al reclutamento). */
  name: z.string().min(1).max(100),
  description: z.string().optional(),
  /**
   * Sotto-tipo (specie del Compagno Animale, elemento del Supporto Elementale,
   * ecc.). Valore semantico dipendente dal template.
   */
  archetype: z.string().optional(),
  stats: FollowerStatsSchema,
  /** Lealtà runtime (gregari). Range tipico 0-2, estremi possibili. */
  loyalty: z.number().int().optional(),
  /** Costo da soddisfare (gregari + Supporto Elementale). */
  cost: FollowerCostSchema.optional(),
  /** Abilità con relativo Rango e stato runtime. */
  skills: z.array(FollowerSkillSchema).default([]),
  /** Capacità utili (lista aperta). */
  traits: z.array(FollowerTraitSchema).default([]),
  /** Inventario del gregario (riusa `ItemSchema`). */
  inventory: z.array(ItemSchema).default([]),
  /** Runtime: PF correnti. */
  currentHp: z.number().int().nonnegative(),
  /** Runtime: vivo / morto / non disponibile. */
  isAlive: z.boolean().default(true),
  /**
   * Regola di "respawn" testuale (manuale-specifica). Esempi: "rituale +
   * 3 condizioni di Magia Improvvisata" (Famiglio); "torna al prossimo
   * tramonto" (Braccio Destro); "nuovo legame con altro animale (GM decide
   * quando)" (Compagno Animale).
   */
  respawnRule: z.string().optional(),
  /**
   * Limiti massimi alle capacità scalanti (manuale r. 5254 per il Compagno
   * Animale: d10 max al dado di Danno, +2 max all'Armatura, +6 max al
   * Carico). Snapshot copiato dal template al momento della creazione del
   * Follower per renderlo auto-contenuto.
   */
  caps: FollowerCapabilityCapsSchema.optional(),
  /**
   * **D8a** — bonus PF scalante al livello del PG derivato dalla peculiarità
   * `hp-bonus-scaling` (Druido «resistente», Sciamano «resistente»). Salvato
   * sul Follower come **snapshot dichiarativo** in modo che
   * `computeFollowerMaxHp` possa ricalcolare il maxHp dinamicamente quando
   * il PG sale di livello, senza dover ri-eseguire `buildFollowerFromTemplate`.
   *
   * Assente se nessuna peculiarità `hp-bonus-scaling` è stata selezionata.
   * Additivo, retrocompat: i Follower FI-8a/FI-8b non hanno il campo.
   */
  hpBonusScaling: z
    .object({
      perCharacterLevel: z.number().int().positive(),
      max: z.number().int().positive(),
    })
    .optional(),
  notes: z.string().default(''),
});
export type Follower = z.infer<typeof FollowerSchema>;

// ============================================================================
// 7. Template del seguace (descrive la mossa-source)
// ============================================================================

/**
 * **FI-8b — D1.5**: effetto eterogeneo di una peculiarità del Follower.
 *
 * Le peculiarità del Famiglio del Mago (manuale V1, rr. 4192-4198) non sono
 * un'unica forma uniforme come i trait del Compagno Animale (FI-8a, tutti
 * con la stessa shape `FollowerTrait`). Sono **eterogenee**: alcune
 * impostano un rank di Abilità, altre un modificatore di Caratteristica
 * selezionato a runtime, altre concedono trait narrativi fissi, altre
 * permettono al giocatore di descrivere una capacità custom (label + desc).
 *
 * Questa discriminated union su `kind` modella i 5 pattern emersi
 * (utilizzati dalle 6 peculiarità del Famiglio + futuri Follower con
 * peculiarità simili):
 *
 *  - **`skill-rank-cumulative`**: imposta un'Abilità a un Rango fisso
 *    (`baseRank`); se l'opzione viene scelta più volte
 *    (`MoveChoice.allowDuplicate: true`), il Rango cresce di 1 per ogni
 *    occorrenza aggiuntiva (cap implicito a R3 dal vincolo di schema).
 *    Es. *Famiglio*: «ha l'Abilità Adepto a Rango 2 (3 se scegli
 *    quest'opzione 2 volte)».
 *
 *  - **`skill-rank-from-narrative`**: come sopra, ma l'Abilità è scelta
 *    dal giocatore tramite un {@link NarrativeField} con `oneOf` enum delle
 *    8 Abilità non-Adepto. Es. *Famiglio*: «ha un'altra Abilità a Rango 1
 *    (2 se scegli questa opzione 2 volte)» — il giocatore scrive «scout»,
 *    «battitore», ecc. nel campo `quale-altra-abilita`.
 *
 *  - **`modifier-from-narrative`**: imposta il modificatore di una stat
 *    scelta dal giocatore. L'`amount` è `baseAmount` per 1× e `baseAmount + 1`
 *    per 2×. Es. *Famiglio*: «ha un modificatore di Caratteristica a +2
 *    (+3 se scegli quest'opzione 2 volte)» — il giocatore sceglie
 *    «forza», «destrezza», ecc.
 *
 *  - **`narrative-trait`**: aggiunge un trait fisso con `label`/`description`
 *    pre-definiti nel template. Niente prompt, niente effetti meccanici
 *    (è puramente narrativo). Es. *Famiglio*: «sa parlare un linguaggio
 *    comprensibile» o «comunicazione telepatica».
 *
 *  - **`custom-trait-from-narrative`**: aggiunge un trait custom con
 *    `label`/`description` letti dai NarrativeField indicati. Se l'opzione
 *    è duplicata (`allowDuplicate: true`), ogni occorrenza usa una
 *    coppia separata di field (`*-label-1`/`*-desc-1` per la 1ª, `*-label-2`/
 *    `*-desc-2` per la 2ª — visibilità via `requiresChoice.minOccurrences`).
 *    Es. *Famiglio*: «ha una capacità utile (concordala con il GM)».
 *
 * **D8a — 3 nuovi `kind` per evocazioni (Supporto Elementale Druido +
 * Animare Zombi Sciamano)** (manuale rr. 3174-3190, 5599-5618):
 *
 *  - **`hp-bonus-scaling`**: aggiunge un bonus PF scalante al livello del PG,
 *    cappato a un valore massimo assoluto. Composto sopra `baseStats.hpRule`
 *    di base (additivo). Es. *Supporto Elementale* «resistente»: +2 PF per
 *    ogni livello del Mago, max +20 PF totali. Il bonus è applicato 1 volta
 *    se selezionato (niente cumulativa: il manuale non lo prevede).
 *
 *  - **`cost-suspension`**: imposta `Follower.cost.suspended = true` al
 *    momento della creazione. Es. *Supporto Elementale* «ben disposto»:
 *    il Druido non deve pagare il Costo per questa evocazione (manuale r. 3186).
 *
 * **Tag intrinseci da archetype** (Druido Supporto Elementale: *fatto-di-fuoco*
 * + *immune-a-fuoco* + *vulnerabile-a-acqua*): **NON** sono modellati come
 * `PeculiarityEffect`. I tag sono **intrinseci** alla scelta archetype
 * (elemento), non un'opzione facoltativa di peculiarità. Modellati su
 * `FollowerTemplate.archetypeTagBundle?` (vedi sotto) — applicati sempre
 * da `buildFollowerFromTemplate` leggendo l'archetype scelto.
 */
export const PeculiarityEffectSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('skill-rank-cumulative'),
    abilityId: FollowerAbilityIdSchema,
    /** Rango base concesso da 1× selezione. 2× porta a `baseRank + 1`. */
    baseRank: z.union([z.literal(1), z.literal(2)]),
    isPrimary: z.boolean().default(false),
  }),
  z.object({
    kind: z.literal('skill-rank-from-narrative'),
    /** ID del NarrativeField con `oneOf` delle Abilità ammissibili. */
    narrativeFieldId: z.string().min(1),
    baseRank: z.union([z.literal(1), z.literal(2)]),
    isPrimary: z.boolean().default(false),
  }),
  z.object({
    kind: z.literal('modifier-from-narrative'),
    /** ID del NarrativeField con `oneOf` delle 6 stat. */
    narrativeFieldId: z.string().min(1),
    /** Valore base del modifier (es. 2 per «+2»). 2× porta a `baseAmount + 1`. */
    baseAmount: StatModifierSchema,
  }),
  z.object({
    kind: z.literal('narrative-trait'),
    /** ID stabile del trait risultante (es. 'famiglio-telepatia'). */
    traitId: z.string().min(1).max(50),
    /** Label fissa nel template (es. "Comunicazione telepatica"). */
    label: z.string().min(1).max(100),
    description: z.string().optional(),
  }),
  z.object({
    kind: z.literal('custom-trait-from-narrative'),
    /** ID del NarrativeField per la `label` quando l'opzione è scelta 1×. */
    labelFieldId: z.string().min(1),
    /** ID opzionale del NarrativeField per la `description` quando l'opzione è scelta 1×. */
    descriptionFieldId: z.string().min(1).optional(),
    /**
     * Field ID alternativi per la 2ª occorrenza (caso `allowDuplicate: true`).
     * Quando definiti, una 2ª selezione dell'opzione produce un trait
     * **separato** (non cumulativo) con label/desc letti da questi field.
     * Visibilità in UI gestita via `NarrativeField.requiresChoice.minOccurrences: 2`.
     */
    labelFieldIdSecond: z.string().min(1).optional(),
    descriptionFieldIdSecond: z.string().min(1).optional(),
  }),
  z.object({
    kind: z.literal('hp-bonus-scaling'),
    /** PF aggiuntivi per ogni livello del PG (es. 2 per «+2 PF per ogni tuo livello»). */
    perCharacterLevel: z.number().int().positive(),
    /** Cap assoluto del bonus totale (es. 20 per «al massimo +20 PF»). */
    max: z.number().int().positive(),
  }),
  z.object({
    kind: z.literal('cost-suspension'),
  }),
]);
export type PeculiarityEffect = z.infer<typeof PeculiarityEffectSchema>;

/**
 * **D8a — `archetypeTagBundle`**: tag intrinseci derivati dalla scelta di
 * archetype del Follower. Non sono una peculiarità facoltativa (manuale
 * Supporto Elementale Druido r. 3180: «*fatto di [elemento], immune a
 * [elemento]* e *vulnerabile a [elemento opposto]*» — sempre presenti).
 *
 * `buildFollowerFromTemplate` legge `selectedChoiceGroups[fromChoiceGroup]`,
 * cerca l'opzione scelta in `optionToTags`, e aggiunge i tag corrispondenti
 * a `Follower.stats.tags`. Se l'opzione non è mappata in `optionToTags`,
 * non aggiunge tag (no-op, niente errore — permette template additivi).
 *
 * Esempio Druido:
 * ```
 * archetypeTagBundle: {
 *   fromChoiceGroup: 'elemento',
 *   optionToTags: {
 *     fuoco: ['fatto-di-fuoco', 'immune-a-fuoco', 'vulnerabile-a-acqua'],
 *     acqua: ['fatto-di-acqua', 'immune-a-acqua', 'vulnerabile-a-fuoco'],
 *     terra: ['fatto-di-terra', 'immune-a-terra', 'vulnerabile-a-aria'],
 *     aria: ['fatto-di-aria', 'immune-a-aria', 'vulnerabile-a-terra'],
 *   },
 * }
 * ```
 */
export const FollowerTemplateArchetypeTagBundleSchema = z.object({
  /** ID del `ChoiceGroup` della mossa-source che contiene l'archetype scelto. */
  fromChoiceGroup: z.string().min(1),
  /**
   * Mappa `id-opzione → tag da aggiungere` a `Follower.stats.tags`.
   * Almeno 1 tag per opzione, almeno 1 carattere per tag.
   */
  optionToTags: z.record(z.string(), z.array(z.string().min(1)).min(1)),
});
export type FollowerTemplateArchetypeTagBundle = z.infer<
  typeof FollowerTemplateArchetypeTagBundleSchema
>;

/**
 * **FI-8b — D1.5**: mappatura ChoiceGroup → effetti eterogenei delle
 * peculiarità del Follower.
 *
 * Diversa da `traitsFromChoiceGroup` (FI-8a) perché ogni opzione del
 * gruppo può avere un `kind` diverso (skill-rank vs modifier vs
 * narrative-trait vs custom-trait). La discriminazione live su
 * `PeculiarityEffectSchema`.
 *
 * Esempio *Famiglio*: `{ fromChoiceGroup: 'main', optionToEffect: {
 *   'adepto-rango-2': { kind: 'skill-rank-cumulative', abilityId: 'adepto', baseRank: 2, isPrimary: true },
 *   'altra-abilita-rango-1': { kind: 'skill-rank-from-narrative', narrativeFieldId: 'quale-altra-abilita', baseRank: 1, isPrimary: false },
 *   ...
 * } }`.
 */
export const FollowerTemplatePeculiaritiesMappingSchema = z.object({
  fromChoiceGroup: z.string().min(1),
  /** Mappa id-opzione → effetto della peculiarità. */
  optionToEffect: z.record(z.string(), PeculiarityEffectSchema),
});
export type FollowerTemplatePeculiaritiesMapping = z.infer<
  typeof FollowerTemplatePeculiaritiesMappingSchema
>;

/**
 * Mappatura: un `ChoiceGroup` della mossa-source determina il bonus +N a una
 * Caratteristica scelta dal giocatore. Le opzioni del gruppo DEVONO avere
 * id corrispondenti a {@link StatIdSchema} ('forza', 'destrezza', …).
 *
 * Esempio Compagno Animale: `{ fromChoiceGroup: 'modificatore', amount: 2 }`
 * con il gruppo "modificatore" della mossa che offre 4 opzioni (FOR/DES/COS/SAG).
 */
export const FollowerTemplateModifierBonusSchema = z.object({
  /** ID del `ChoiceGroup` nella mossa-source che contiene le opzioni di stat. */
  fromChoiceGroup: z.string().min(1),
  /** Quanto sommare al mod dello stat scelto (+2 tipico, +3 con upgrade). */
  amount: StatModifierSchema,
});
export type FollowerTemplateModifierBonus = z.infer<typeof FollowerTemplateModifierBonusSchema>;

/**
 * Mappatura: un `ChoiceGroup` della mossa-source contiene le opzioni di
 * capacità utile selezionabili; ogni id-opzione corrisponde a un
 * {@link FollowerTrait} da applicare al gregario.
 *
 * Esempio Compagno Animale: `{ fromChoiceGroup: 'capacita', optionToTrait: {
 * 'feroce': {...trait feroce...}, 'tenace': {...}, 'resistente': {...} } }`.
 */
export const FollowerTemplateTraitMappingSchema = z.object({
  fromChoiceGroup: z.string().min(1),
  /** Mappa id-opzione → trait completo. */
  optionToTrait: z.record(z.string(), FollowerTraitSchema),
});
export type FollowerTemplateTraitMapping = z.infer<typeof FollowerTemplateTraitMappingSchema>;

/**
 * **FI-8a — custom input per archetype**. Quando il giocatore seleziona
 * l'opzione `whenChoice` nel gruppo `fromChoiceGroup` (tipicamente
 * `'altro'`), `Follower.archetype` viene popolato dal valore del
 * `NarrativeField` indicato invece che dall'ID dell'opzione.
 *
 * Esempio Compagno Animale: il giocatore può scegliere una specie esotica
 * concordata con il GM digitando il nome in un input testuale.
 */
export const FollowerTemplateCustomArchetypeSchema = z.object({
  fromChoiceGroup: z.string().min(1),
  /** ID dell'opzione che attiva la modalità custom (tipicamente `'altro'`). */
  whenChoice: z.string().min(1),
  /** ID del `NarrativeField` da cui prendere il valore custom. */
  narrativeFieldId: z.string().min(1),
});
export type FollowerTemplateCustomArchetype = z.infer<typeof FollowerTemplateCustomArchetypeSchema>;

/**
 * **FI-8a — custom input per capacità utile**. Quando il giocatore seleziona
 * l'opzione `whenChoice` nel gruppo `fromChoiceGroup` (tipicamente
 * `'altro'`), invece di applicare un trait predefinito dalla
 * `traitsFromChoiceGroup.optionToTrait`, il sistema crea un trait con
 * `label` letto dal `labelFieldId` e `description` opzionale dal
 * `descriptionFieldId`. Il trait creato **non ha `effects`**: il manuale
 * dice esplicitamente «concorda con il GM» — l'effetto meccanico va
 * negoziato a tavolo, non standardizzato dal sistema.
 */
export const FollowerTemplateCustomTraitSchema = z.object({
  fromChoiceGroup: z.string().min(1),
  /** ID dell'opzione che attiva la modalità custom (tipicamente `'altro'`). */
  whenChoice: z.string().min(1),
  /** ID del `NarrativeField` per il `label` della capacità custom. */
  labelFieldId: z.string().min(1),
  /** ID opzionale del `NarrativeField` per la `description`. */
  descriptionFieldId: z.string().min(1).optional(),
});
export type FollowerTemplateCustomTrait = z.infer<typeof FollowerTemplateCustomTraitSchema>;

/**
 * **D8a — recruitRoll**: alcune mosse-source (evocazioni) si attivano con
 * un tiro al reclutamento il cui esito determina **quante peculiarità** il
 * giocatore può selezionare. Manuale rr. 3174-3190 (Supporto Elementale
 * Druido: tira+SAG, 10+ → 4 scelte; 7-9 → 2 scelte) e rr. 5599-5618
 * (Animare Zombi Sciamano: tira+CAR, 10+ → tutte le 4; 7-9 → 2).
 *
 * Il `recruitOutcome` (`'10+' | '7-9'`) viene memorizzato su `MoveRef`
 * della mossa-source (vedi `MoveRefSchema.recruitOutcome` in
 * `src/schemas/character.ts`) come scelta permanente del giocatore al
 * reclutamento — necessaria per:
 *  1. determinare il `pickCount` effettivo del `ChoiceGroup` di peculiarità
 *     al render del setup (cap dinamico delle selezioni);
 *  2. audit/replay del setup (decisione SSOT §8.1 D8 «Sì — salvare l'esito»).
 *
 * Il fallimento (`6-`) NON è modellato qui: la mossa stessa, in caso di
 * fallimento, non concede il follower (resta description-only nel testo).
 */
export const FollowerTemplateRecruitRollSchema = z.object({
  /** Stat del tiro (es. `'saggezza'` per il Druido). */
  stat: StatIdSchema,
  /** Numero di peculiarità selezionabili con esito 10+ (manuale: 4 tipico). */
  onSuccess: z.object({ choicesCount: z.number().int().positive() }),
  /** Numero di peculiarità selezionabili con esito 7-9 (manuale: 2 tipico). */
  onPartial: z.object({ choicesCount: z.number().int().positive() }),
});
export type FollowerTemplateRecruitRoll = z.infer<typeof FollowerTemplateRecruitRollSchema>;

/**
 * **D8b — `primarySkillFromNarrativeField`**: l'Abilità primaria del
 * Follower è scelta dal giocatore al reclutamento tramite un
 * {@link NarrativeField} con `oneOf` enum delle 9 Abilità. Pattern usato
 * per il **Braccio Destro del Warlock** (manuale r. 6001: «scegli per il
 * servitore un'Abilità primaria a Rango 1»).
 *
 * Distinto da `peculiaritiesFromChoiceGroup` (FI-8b): l'Abilità è
 * **sempre** scelta (1 occorrenza obbligatoria), non opzionale.
 * Distinto da `fixedSkills` (FI-8a): l'`abilityId` è dinamico
 * (letto dal narrativeField), non hardcoded nel template.
 */
export const FollowerTemplatePrimarySkillFromNarrativeFieldSchema = z.object({
  /** ID del NarrativeField con `oneOf` delle 9 Abilità. */
  narrativeFieldId: z.string().min(1),
  /** Rango iniziale dell'Abilità (es. 1 per Braccio Destro base). */
  rank: FollowerSkillRankSchema,
});
export type FollowerTemplatePrimarySkillFromNarrativeField = z.infer<
  typeof FollowerTemplatePrimarySkillFromNarrativeFieldSchema
>;

/**
 * **D8b — `traitFromNarrativeFields`**: una capacità utile **sempre
 * presente** sul Follower, con `label` e `description` letti da
 * NarrativeField del giocatore. Pattern usato per il **Braccio Destro del
 * Warlock** (manuale r. 6001: «una capacità utile da concordare con il GM»).
 *
 * Distinto da `traitCustomFromNarrativeField` (FI-8a): quel pattern attiva
 * il trait solo quando il giocatore sceglie l'opzione "altro" in un
 * ChoiceGroup specifico (es. Compagno Animale specie "altro"). Qui invece
 * il trait è **sempre** creato.
 */
export const FollowerTemplateTraitFromNarrativeFieldsSchema = z.object({
  /** ID stabile del trait risultante (es. 'braccio-destro-capacita'). */
  traitId: z.string().min(1).max(50),
  /** ID del NarrativeField per la `label` del trait. */
  labelFieldId: z.string().min(1),
  /** ID opzionale del NarrativeField per la `description`. */
  descriptionFieldId: z.string().min(1).optional(),
});
export type FollowerTemplateTraitFromNarrativeFields = z.infer<
  typeof FollowerTemplateTraitFromNarrativeFieldsSchema
>;

/**
 * **D8c — `FollowerTemplateUpgradePeculiaritiesMapping`** (FI-41): variante
 * di {@link FollowerTemplatePeculiaritiesMappingSchema} con `optionToEffect`
 * come **array** di {@link PeculiarityEffect} invece che singolo. Necessaria
 * per supportare "1 opzione → N effetti" del Servitore Competente Warlock
 * (manuale r. 6019: opzione 1 «scegli due Abilità secondarie a Rango 1» = 2
 * `skill-rank-from-narrative` distinte sulla stessa opzione; opzione 2
 * «una a Rango 1 e una nuova capacità utile» = 1 `skill-rank-from-narrative`
 * + 1 `custom-trait-from-narrative`).
 *
 * Distinto dal mapping del template-base (`FollowerTemplatePeculiaritiesMappingSchema`,
 * singolo effect per opzione, usato dal Famiglio del Mago) perché il
 * pattern delle scelte sull'upgrade è strutturalmente diverso (multi-effect
 * canonico). Famiglio non viene migrato — il singolo-effect basta per i
 * suoi casi e il cambio sarebbe pure rumore.
 */
export const FollowerTemplateUpgradePeculiaritiesMappingSchema = z.object({
  fromChoiceGroup: z.string().min(1),
  /**
   * Mappa id-opzione → array di effetti applicati come delta al Follower
   * esistente. Minimo 1 effetto per opzione.
   */
  optionToEffect: z.record(z.string(), z.array(PeculiarityEffectSchema).min(1)),
});
export type FollowerTemplateUpgradePeculiaritiesMapping = z.infer<
  typeof FollowerTemplateUpgradePeculiaritiesMappingSchema
>;

/**
 * **D8b — `FollowerTemplate.upgrades`**: una mossa advanced del PG può
 * applicare un override declarativo al template-base del Follower. Pattern
 * paga-una-volta-sola: introdotto in D8b per *Il Khan dei Khan* del Barbaro
 * (upgrade della Banda di Guerra: livello 2→3, HP 5→8, mod +2→+3).
 *
 * **D8c — FI-41**: estensione cumulativa per coprire la chain advanced
 * Warlock (Servitore Competente + Maestria del Servitore) e Ranger (Ben
 * Addestrato + Alleato Innaturale) tramite 3 campi additivi opzionali:
 *  - {@link primarySkillRankOverride}: porta a un nuovo Rango la primary
 *    skill dichiarata via `template.primarySkillFromNarrativeField`
 *    (Servitore Competente: R1→R2; Maestria del Servitore: R2→R3).
 *  - {@link additionalAlwaysTraitsFromNarrativeFields}: capacità utili
 *    sempre presenti aggiuntive dall'upgrade (Ben Addestrato +2; Alleato
 *    Innaturale +2). Riusa `FollowerTemplateTraitFromNarrativeFieldsSchema`
 *    come tipo dell'elemento.
 *  - {@link peculiaritiesFromChoiceGroup}: peculiarità choice-based con
 *    multi-effect (Servitore Competente, Maestria). Dispatch via
 *    `applyPeculiarities` ma leggendo da `MoveRef` della mossa di upgrade
 *    invece che della mossa-base.
 *
 * **Composizione**: gli override **statici** (level/hp/mod/primary-rank)
 * modificano `effectiveTemplate`; gli effetti **additivi** (trait
 * always-present + peculiarities) sono prodotti come delta runtime e
 * accumulati nel Follower finale dal service `buildFollowerFromTemplate`.
 * Le proprietà non specificate (`undefined`) preservano i valori originali.
 */
export const FollowerTemplateUpgradeSchema = z.object({
  /**
   * ID della mossa advanced del PG che, se posseduta, attiva l'upgrade.
   * Es. `'barbaro.il-khan-dei-khan'`.
   */
  fromMoveId: z.string().min(1),
  /**
   * Nuovo livello del gregario (override di `baseStats.level`). Es. Khan
   * dei Khan: 2 → 3.
   */
  levelOverride: FollowerLevelSchema.optional(),
  /**
   * Nuova regola PF (override di `baseStats.hpRule`). Es. Khan dei Khan:
   * `{ type: 'fixed', value: 5 }` → `{ type: 'fixed', value: 8 }`.
   */
  hpRuleOverride: FollowerHpRuleSchema.optional(),
  /**
   * Nuovo modifier bonus (override di `template.modifierBonus.amount`).
   * Es. Khan dei Khan: +2 → +3 sulla stessa stat scelta dal giocatore.
   * Lo `fromChoiceGroup` del nuovo bonus deve combaciare con quello
   * originale (la stat scelta resta la stessa: il bonus aumenta).
   */
  modifierBonusOverride: FollowerTemplateModifierBonusSchema.optional(),
  /**
   * **D8c — FI-41**: nuovo Rango della primary skill dichiarata via
   * `template.primarySkillFromNarrativeField`. Applicato come override
   * sul campo `rank` del sub-schema. Esempi: Servitore Competente Warlock
   * R1→R2, Maestria del Servitore R2→R3. Richiede che il template-base
   * abbia `primarySkillFromNarrativeField` definito (Braccio Destro Warlock).
   */
  primarySkillRankOverride: FollowerSkillRankSchema.optional(),
  /**
   * **D8c — FI-41**: capacità utili sempre presenti **aggiuntive**
   * dall'upgrade. Lista (tipicamente 1-2 entry). Riusa al 100% il
   * sub-schema D8b `FollowerTemplateTraitFromNarrativeFieldsSchema` come
   * tipo dell'elemento — ogni entry richiede `traitId`, `labelFieldId` e
   * opzionalmente `descriptionFieldId` letti dal `MoveRef` della mossa di
   * upgrade. Esempi: Ben Addestrato Ranger (2 capacità custom per il
   * Compagno Animale), Alleato Innaturale Ranger (2 capacità custom).
   * Default `[]` per retrocompat con D8b (Khan dei Khan non lo usa).
   */
  additionalAlwaysTraitsFromNarrativeFields: z
    .array(FollowerTemplateTraitFromNarrativeFieldsSchema)
    .default([]),
  /**
   * **D8c — FI-41**: peculiarità choice-based applicate al Follower
   * esistente quando il giocatore sceglie sulla mossa di upgrade. Diversa
   * da `FollowerTemplate.peculiaritiesFromChoiceGroup` (template-base,
   * singolo effect): qui `optionToEffect: Record<string, PeculiarityEffect[]>`
   * (array) per supportare "1 opzione → N effetti" (es. Servitore Competente
   * opzione 1 «2 Abilità secondarie R1» = 2 `skill-rank-from-narrative`
   * sulla stessa opzione, con `narrativeFieldId` distinti per le 2 sec).
   *
   * Dispatch via `applyPeculiarities` riutilizzato dal modulo
   * `followerPeculiarities.ts`, ma con input ricostruito dal `MoveRef`
   * della mossa di upgrade (`upgradeMoveRefs[upgrade.fromMoveId]`).
   */
  peculiaritiesFromChoiceGroup: FollowerTemplateUpgradePeculiaritiesMappingSchema.optional(),
});
export type FollowerTemplateUpgrade = z.infer<typeof FollowerTemplateUpgradeSchema>;

/**
 * Descrizione completa di un template di seguace (la "ricetta" di una
 * mossa-source). Il giocatore, al momento di prendere la mossa, compila
 * le scelte definite dal `MoveSchema.choiceGroups` / `narrativeFields`
 * della mossa-source; il service `buildFollowerFromTemplate` produce un
 * `Follower` valido combinando i `baseStats` del template + le scelte.
 *
 * **Riuso `MoveSchema.choiceGroups`** (FI-18 Fase 2): le scelte al
 * reclutamento NON sono ridefinite qui; sono quelle già presenti sulla
 * mossa-source. Il template mappa solo "quale gruppo determina cosa".
 */
export const FollowerTemplateSchema = z.object({
  /** ID stabile del template (es. 'ranger-compagno-animale'). */
  id: z.string().min(1).max(80),
  /** ID della mossa-source nel catalogo mosse. */
  sourceMoveId: z.string().min(1),
  /** Tipo del Follower prodotto. */
  type: FollowerTypeSchema,
  /** Etichetta visibile (es. "Compagno Animale"). */
  label: z.string().min(1).max(100),
  /** Statistiche base del gregario al reclutamento (prima delle scelte). */
  baseStats: FollowerStatsSchema,
  /**
   * Lealtà di default per i gregari (manuale r. 1303: «di solito **1**»).
   * Assente per evocazioni/animal-companion.
   */
  defaultLoyalty: z.number().int().optional(),
  /**
   * Motivazione del Costo, fissata dalla mossa-source (es. «portare avanti i
   * piani del patrono» per il Braccio Destro). Assente per template senza
   * Costo.
   */
  costMotivation: z.string().min(1).max(200).optional(),
  /**
   * Abilità preset, fissate dalla mossa-source (manuale: il Famiglio del
   * Mago parte con Adepto R1). Possono essere ulteriormente potenziate dalle
   * scelte al reclutamento.
   */
  fixedSkills: z.array(FollowerSkillSchema).default([]),
  /**
   * Capacità utili fisse (es. capacità peculiari della Banda di Guerra del
   * Barbaro: "subisce danno → -1 PF", "infligge danni pari ai PF", …).
   */
  fixedTraits: z.array(FollowerTraitSchema).default([]),
  /**
   * Mappatura "ChoiceGroup → +N a uno stat" (riferimento alle scelte della
   * mossa-source). Assente per template senza bonus mod (Banda di Guerra ha
   * il bonus ma non è scelto dal giocatore: è fisso "uno a +2"; in tal caso
   * va trattato come bonus narrativo, non come scelta).
   */
  modifierBonus: FollowerTemplateModifierBonusSchema.optional(),
  /**
   * ID di un `ChoiceGroup` della mossa-source il cui valore scelto popola
   * `Follower.archetype` (es. "Specie" del Compagno Animale → archetype
   * = 'lupo'). L'id-opzione del gruppo diventa il valore di `archetype`.
   */
  archetypeFromChoiceGroup: z.string().min(1).optional(),
  /**
   * Mappatura "ChoiceGroup di capacità → traits da aggiungere". Per ogni
   * id-opzione scelto dal giocatore nel gruppo indicato, il `Follower`
   * riceve il `FollowerTrait` corrispondente da `optionToTrait`.
   */
  traitsFromChoiceGroup: FollowerTemplateTraitMappingSchema.optional(),
  /**
   * **FI-8b — D1.5**: mappatura "ChoiceGroup di peculiarità → effetti
   * eterogenei". Diversa da {@link traitsFromChoiceGroup} perché ciascuna
   * opzione può produrre un effetto di `kind` diverso (skill-rank,
   * modifier, narrative-trait, custom-trait). Utilizzata per il
   * **Famiglio del Mago**, in cui le 6 peculiarità sono eterogenee.
   *
   * Quando l'opzione ha `MoveChoice.allowDuplicate: true` (predisposto da
   * D1), una doppia selezione produce un effetto cumulativo per i
   * `kind: 'skill-rank-*'` e `'modifier-from-narrative'` (rank/amount +1),
   * oppure un trait separato per `kind: 'custom-trait-from-narrative'`
   * (via campi `*Second` del NarrativeField).
   */
  peculiaritiesFromChoiceGroup: FollowerTemplatePeculiaritiesMappingSchema.optional(),
  /**
   * **FI-8a custom input per archetype**: override del valore di
   * `Follower.archetype` quando l'opzione attiva nel gruppo è quella
   * "custom" (es. specie "altro"). Vedi {@link FollowerTemplateCustomArchetypeSchema}.
   */
  archetypeCustomFromNarrativeField: FollowerTemplateCustomArchetypeSchema.optional(),
  /**
   * **FI-8a custom input per capacità utile**: invece di applicare un trait
   * mappato, crea un trait custom con label/description dai narrativeField
   * indicati. Vedi {@link FollowerTemplateCustomTraitSchema}.
   */
  traitCustomFromNarrativeField: FollowerTemplateCustomTraitSchema.optional(),
  /**
   * ID di un `NarrativeField` della mossa-source il cui valore popola
   * `Follower.name`. Es. "nome-compagno" → il giocatore digita il nome del
   * Compagno Animale, che diventa `Follower.name`.
   */
  nameFromNarrativeField: z.string().min(1).optional(),
  /**
   * Limiti massimi per le capacità scalanti (vedi {@link FollowerCapabilityCapsSchema}).
   */
  capabilityCaps: FollowerCapabilityCapsSchema.optional(),
  /**
   * **D8a**: configurazione del tiro al reclutamento (per evocazioni con
   * pickCount dinamico). Quando definito, il giocatore deve dichiarare
   * l'esito del tiro (`MoveRef.recruitOutcome`) prima di selezionare le
   * peculiarità; il `pickCount` del `ChoiceGroup` di peculiarità viene
   * derivato runtime da `onSuccess.choicesCount` (10+) o `onPartial.choicesCount`
   * (7-9). Vedi {@link FollowerTemplateRecruitRollSchema}.
   */
  recruitRoll: FollowerTemplateRecruitRollSchema.optional(),
  /**
   * **D8a**: tag intrinseci derivati dalla scelta di archetype (es. *Supporto
   * Elementale* Druido: archetype `'fuoco'` → tag `*fatto-di-fuoco*`,
   * `*immune-a-fuoco*`, `*vulnerabile-a-acqua*`). Applicati sempre da
   * `buildFollowerFromTemplate`, non opzionali. Vedi
   * {@link FollowerTemplateArchetypeTagBundleSchema}.
   */
  archetypeTagBundle: FollowerTemplateArchetypeTagBundleSchema.optional(),
  /**
   * **D8b**: Abilità primaria del Follower scelta dal giocatore via
   * NarrativeField con `oneOf` 9 Abilità (es. *Braccio Destro* Warlock —
   * manuale r. 6001). Pattern alternativo a `fixedSkills` (statiche) e
   * `peculiaritiesFromChoiceGroup` (opzionali). Vedi
   * {@link FollowerTemplatePrimarySkillFromNarrativeFieldSchema}.
   */
  primarySkillFromNarrativeField: FollowerTemplatePrimarySkillFromNarrativeFieldSchema.optional(),
  /**
   * **D8b**: capacità utile **sempre presente** sul Follower, con
   * `label`/`description` letti da NarrativeField del giocatore. Pattern
   * usato per il *Braccio Destro* Warlock (1 capacità utile concordata con
   * il GM). Vedi {@link FollowerTemplateTraitFromNarrativeFieldsSchema}.
   */
  traitFromNarrativeFields: FollowerTemplateTraitFromNarrativeFieldsSchema.optional(),
  /**
   * **D8b**: regole di upgrade dichiarative attivate da mosse advanced del
   * PG. Es. *Il Khan dei Khan* del Barbaro modifica la Banda di Guerra:
   * livello 2→3, HP 5→8, mod +2→+3. Applicato statico da `applyUpgrades`
   * (`followerCreation.ts`) leggendo `character.moves`. Default: `[]` (no
   * upgrade). Vedi {@link FollowerTemplateUpgradeSchema}.
   */
  upgrades: z.array(FollowerTemplateUpgradeSchema).default([]),
  /**
   * Regola testuale di "respawn" del gregario (manuale-specifica). Salvata sul
   * `Follower` istanziato per riferimento del giocatore.
   */
  respawnRule: z.string().optional(),
});
export type FollowerTemplate = z.infer<typeof FollowerTemplateSchema>;
